import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  FixedCol,
  HubTitle,
  Page,
  Topbar,
  TopbarSelect,
  TopbarTitle,
} from '@runtime/components/layout/PageLayout'
import { getAvailableLocales, useI18n } from '@runtime/core/i18n/i18n'
import ProfileButton from '@runtime/features/profile/ProfileButton'
import CatalogIntegrationsPanel from '@studio/features/catalog-integrations/CatalogIntegrationsPanel'
import PrepressJobsPanel from '@studio/features/prepress-jobs/PrepressJobsPanel'

import HubAppHomeButton from './HubAppHomeButton'
import styles from './HubPageShell.module.scss'
import { requestStudioNavigation } from './studioNavigationRequest'

type HubPageShellProps = {
  hero: ReactNode
  children: ReactNode
  showProfileButton?: boolean
  showHomeButton?: boolean
}

export default function HubPageShell({
  hero,
  children,
  showProfileButton = true,
  showHomeButton = true,
}: HubPageShellProps) {
  const navigate = useNavigate()
  const { t, locale, setLocale } = useI18n()

  return (
    <Page variant="hub">
      <FixedCol>
        <Topbar>
          <div className="left">{showProfileButton ? <ProfileButton /> : null}</div>

          <div className="right">
            <TopbarSelect
              className="s"
              value={locale}
              onChange={event => setLocale(event.target.value)}
              aria-label={t('common.language')}
            >
              {getAvailableLocales().map(nextLocale => (
                <option key={nextLocale} value={nextLocale}>
                  {nextLocale.toUpperCase()}
                </option>
              ))}
            </TopbarSelect>
          </div>

          {showHomeButton ? (
            <TopbarTitle className={styles.topbarHomeAction}>
              <HubAppHomeButton onClick={() => requestStudioNavigation(() => navigate('/'))} />
            </TopbarTitle>
          ) : null}
        </Topbar>

        <HubTitle className={styles.hero}>{hero}</HubTitle>

        <PrepressJobsPanel />
        <CatalogIntegrationsPanel />
        {children}
      </FixedCol>
    </Page>
  )
}
